import React, { useEffect, useRef, useState, useCallback } from "react";
import { Row, Col, Form, Button, Table, Modal, Spinner } from "react-bootstrap";
import { Link, useNavigate, useParams, useBlocker } from "react-router-dom";
import axios from "axios";
import { IoEyeOutline } from "react-icons/io5";
import { ErrorMessage, FieldArray, Formik } from "formik";
import * as Yup from "yup";
import { jwtDecode } from "jwt-decode";
import Card from "./Card.js";
import url from "../configs/config";
import { IconButton, Tooltip } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import FocusError from "./error/error-focus";
import AlertNotificationSimple from "./alert/alert-notification-simple";
import "react-phone-input-2/lib/style.css";
import Select from "react-select";
import FadeLoaderComponent from "./loader-component";
import { BsCaretUpFill, BsCaretDownFill } from "react-icons/bs";
import * as SettingSelector from "../store/setting/selectors";
import { useSelector } from "react-redux";
import { roleTypes } from "../data/roleType.js";
import AddProductsModal from "./models/add-products-model.js";

const JobBomAdd = () => {
  const alertRef = useRef(null);
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState({});
  const [decodedToken, setDecodedToken] = useState();
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState(null);
  const [warehouseAddress, setWarehouseAddress] = useState("");
  const [selectedProductListIds, setSelectedProductListIds] = useState([]);
  const token = localStorage.getItem("coyote-token");
  const [systems, setSystems] = useState([]);
  const [phases, setPhases] = useState([]);
  const [searchSelectedProducts, setSearchSelectedProducts] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [optionsSystem, setOptionsSystem] = useState([]);
  const [optionsPhase, setOptionsPhase] = useState([]);
  const permissions = useSelector(SettingSelector?.set_permissions);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [inactiveProductErrors, setInactiveProductErrors] = useState([]);
  const [activeSystemId, setActiveSystemId] = useState(null);
  const [activePhaseId, setActivePhaseId] = useState(null);
  const [isMerging, setIsMerging] = useState(false);

  const [selectedSystemForFilter, setSelectedSystemForFilter] = useState(null);
  const [selectedPhaseForFilter, setSelectedPhaseForFilter] = useState(null);
  const productHasCreatePermission =
    permissions?.product_request?.create === true;
  const warehouseHasCreatePermission = permissions?.warehouse?.create === true;
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  const [sortConfigProductRequest, setSortConfigProductRequest] = useState({
    key: "productName",
    direction: "asc",
  });

  const [formData, setFormData] = useState({
    warehouseId: null,
    items: [],
  });

  const [selectedAddedProducts, setSelectedAddedProducts] = useState([]);
  const [itemView, setItemView] = useState();
  const [itemViewIndex, setItemViewIndex] = useState();
  const [valuesData, setValuesData] = useState();
  const [itemViewModel, setItemViewModel] = useState(false);
  const [productsViewModel, setProductsViewModel] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [itemUpdateEnabled, setItemUpdateEnabled] = useState(false);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [clickSubmitProduct, setClickSubmitProduct] = useState(false);
  const [backendValidationErrors, setBackendValidationErrors] = useState([]);
  const [isLoadingMoreSelectedProduct, setIsLoadingMoreSelectedProduct] =
    useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  const createUniqueKey = (productId, systemId, phaseId) => {
    return `${productId}-${systemId || "null"}-${phaseId || "null"}`;
  };

  const mergeProducts = (existingProducts, newProducts) => {
    const newProductsToAdd = [];
    const existingProductsMap = new Map();

    existingProducts.forEach((product) => {
      const key = createUniqueKey(
        product.productId,
        product.systemId,
        product.phaseId
      );
      existingProductsMap.set(key, { ...product });
    });

    newProducts.forEach((product) => {
      const key = createUniqueKey(
        product.productId,
        product.systemId,
        product.phaseId
      );

      if (existingProductsMap.has(key)) {
        const existingProduct = existingProductsMap.get(key);
        const newQuantity =
          (parseInt(existingProduct.quantity) || 0) +
          (parseInt(product.quantity) || 0);

        const newRate = parseFloat(existingProduct.rate) || 0;
        const newAmount = (newQuantity * newRate).toFixed(2);

        const mergedProduct = {
          ...existingProduct,
          ...product,
          quantity: newQuantity,
          rate: newRate,
          amount: parseFloat(newAmount),
        };

        existingProductsMap.delete(key);
        newProductsToAdd.push(mergedProduct);
      } else {
        newProductsToAdd.push({
          ...product,
          quantity: parseInt(product.quantity) || 0,
          rate: parseFloat(product.rate) || 0,
          amount: parseFloat(product.amount) || 0,
        });
      }
    });

    const remainingExistingProducts = Array.from(existingProductsMap.values());

    return [...newProductsToAdd, ...remainingExistingProducts];
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  useEffect(() => {
    if (selectedAddedProducts.length > 0) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [selectedAddedProducts]);

  useEffect(() => {
    if (selectedSystemForFilter) {
      const selectedSystem = systems.find(
        (sys) => sys.system_id === selectedSystemForFilter.value
      );
      const filteredPhases = selectedSystem?.phases || [];

      const phaseOptions = filteredPhases.map((phase) => ({
        value: phase.phase_id,
        label: phase.phase_name,
      }));

      setOptionsPhase(phaseOptions);

      if (
        selectedPhaseForFilter &&
        !phaseOptions.find((p) => p.value === selectedPhaseForFilter.value)
      ) {
        setSelectedPhaseForFilter(null);
      }
    } else {
      setOptionsPhase([]);
      setSelectedPhaseForFilter(null);
    }
  }, [selectedSystemForFilter, systems]);

  useEffect(() => {
    const warehouseOptions =
      warehouses.length > 0 &&
      warehouses.map((warehouse) => ({
        value: warehouse.id,
        label: warehouse.name,
        shipTo: warehouse.address,
      }));
    setWarehouseOptions(warehouseOptions || []);
  }, [warehouses]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchSelectedProducts, selectedSystemForFilter, selectedPhaseForFilter]);

  const getWarehouses = async () => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(
        `${url.backendURL}user/warehouse-list`,
        config
      );
      setWarehouses(response.data.response.data);
      if (decodedToken?.warehouse_id) {
        setWarehouseId(decodedToken?.warehouse_id);
        setWarehouseAddress(
          response.data.response.data.find(
            (warehouse) => warehouse.id === decodedToken?.warehouse_id
          )?.address
        );
        setFormData({
          ...formData,
          warehouseId: decodedToken?.warehouse_id,
        });
      }
    } catch (err) {
      // handleError(err, "user-edit");
    }
  };

  const getSystems = async () => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(
        `${url.backendURL}job/${jobId}/systems-with-phases`,
        config
      );

      const systemsData = response.data.response.data.systems || [];
      setSystems(systemsData);

      const allPhases = systemsData.flatMap((system) => system.phases || []);
      setPhases(allPhases);

      const systemOptions = systemsData.map((system) => ({
        value: system.system_id,
        label: system.system_name,
      }));
      setOptionsSystem(systemOptions);

      const fetchedActiveSystemId =
        response.data.response.data.active_system_id;
      const fetchedActivePhaseId = response.data.response.data.active_phase_id;
    } catch (err) {
      handleError(err, "job-bom-add");
    }
  };

  useEffect(() => {
    if (token && decodedToken && jobId) {
      const fetchData = async () => {
        await getWarehouses();
        await getSystems();
        setLoader(false);
      };
      fetchData();
    }
  }, [token, decodedToken, jobId]);

  const handleSubmit = async (
    values,
    { setSubmitting, resetForm, setTouched, setErrors }
  ) => {
    setErrors({});
    setTouched({});
    setBackendValidationErrors([]);
    setSubmitting(true);

    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };

      const sanitizedValues = {
        warehouseId: warehouseId,
        products: values.items.map((item) => {
          const {
            productName,
            manufacturerName,
            description,
            model,
            manufacturerId,
            sku,
            ...sanitizedItem
          } = item;
          return {
            ...sanitizedItem,
            quantityRequired: sanitizedItem.quantity,
            unitCost: sanitizedItem.rate,
            productTotalCost: sanitizedItem.amount,
          };
        }),
      };

      const response = await axios.post(
        `${url.backendURL}job/${jobId}/bom/`,
        sanitizedValues,
        config
      );

      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setHasUnsavedChanges(false);
      setInactiveProductErrors([]);
      setAlert({
        message: response.data.message,
        type: "success",
        show: true,
        alertFor: "job-bom-add",
      });
      setErrors({});
      setHasUnsavedChanges(false);
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
        navigate(`/dashboard/app/job-edit/${jobId}`);
      }, 2000);
    } catch (err) {
      if (err.response?.data?.response?.details) {
        setBackendValidationErrors(err.response.data.response.details);
      }
      if (err.response?.data?.response?.data) {
        const inactiveProducts = err.response.data.response.data.map(
          (error) => error.productId
        );
        setInactiveProductErrors(inactiveProducts);
      }
      handleError(err, "job-bom-add");
    } finally {
      setSubmitting(false);
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${decodedToken?.roles?.some(
          (role) => role.role_type === roleTypes.SUPER_ADMIN
        )
          ? "admin"
          : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const handleSystemPhaseChange = (
    items,
    index,
    field,
    value,
    setFieldValue
  ) => {
    setIsMerging(true);

    setTimeout(() => {
      const updatedItems = [...items];
      const currentItem = { ...updatedItems[index] };

      if (field === "systemId") {
        currentItem.systemId = value;
        currentItem.phaseId = null;
      } else if (field === "phaseId") {
        currentItem.phaseId = value;
      }

      const duplicateIndex = updatedItems.findIndex(
        (item, idx) =>
          idx !== index &&
          item.productId === currentItem.productId &&
          item.systemId === currentItem.systemId &&
          item.phaseId === currentItem.phaseId
      );

      if (duplicateIndex !== -1) {
        const existingItem = updatedItems[duplicateIndex];
        existingItem.quantity =
          (parseInt(existingItem.quantity) || 0) +
          (parseInt(currentItem.quantity) || 0);
        existingItem.amount = (
          existingItem.quantity * (parseFloat(existingItem.rate) || 0)
        ).toFixed(2);

        updatedItems.splice(index, 1);
      } else {
        updatedItems[index] = currentItem;
      }

      setFieldValue("items", updatedItems);
      setSelectedAddedProducts(updatedItems);
      setIsMerging(false);
    }, 300);
  };

  const validationSchema = () =>
    Yup.object().shape({
      warehouseId: Yup.number()
        .integer("Warehouse must be a valid")
        .positive("Warehouse is invalid")
        .typeError("Warehouse is invalid")
        .required("Warehouse is required"),

      items: Yup.array()
        .of(
          Yup.object().shape({
            productId: Yup.number()
              .integer()
              .required("Product is required")
              .typeError("Product must be a valid"),
            manufacturerId: Yup.number()
              .integer()
              .required("Manufacturer is required")
              .typeError("Manufacturer must be a valid"),
            systemId: Yup.number()
              .integer()
              .required("System is required")
              .typeError("System must be a valid"),
            phaseId: Yup.number()
              .integer()
              .required("Phase is required")
              .typeError("Phase must be a valid"),
            quantity: Yup.number()
              .required("Quantity is required")
              .integer("Quantity must be an integer")
              .typeError("Quantity must be a number")
              .positive("Quantity must be greater than 0"),
            rate: Yup.number()
              .required("Rate is required")
              .typeError("Rate must be a number")
              .moreThan(0, "Rate must be greater than 0")
              .max(9999999999999.99, "Rate cannot exceed 9999999999999.99")
              .transform((value) => (value === "" ? null : value))
              .test(
                "is-decimal",
                "Rate must have a maximum of 2 decimal places",
                (value) => {
                  if (value === null) return true;
                  const isValid = /^[+-]?\d+(\.\d{1,2})?$/.test(
                    value.toString()
                  );
                  return isValid;
                }
              )
              .test(
                "max-digits",
                "Rate cannot have more than 13 digits before decimal point",
                (value) => {
                  if (value === null) return true;
                  const [integerPart] = value.toString().split(".");
                  return integerPart.replace("-", "").length <= 13;
                }
              ),
            amount: Yup.number()
              .required("Amount is required")
              .typeError("Amount must be a number"),
          })
        )
        .min(1, "At least one item is required")
        .required("Items are required"),
    });

  const fetchProductSuggestions = async (
    inputValue,
    selectedProductIds = [],
    keyCol,
    directionCol,
    limit,
    offset
  ) => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
        params: {
          product: inputValue,
          searchKey: keyCol,
          searchDirection: directionCol,
          limit: limit,
          offset: offset,
        },
      };

      const response = await axios.get(`${url.backendURL}job/products`, config);
      const products = response.data.response.data;
      const pagination = response.data.response.pagination;

      const productsData = {
        products: products.map((product) => ({
          productId: product.product_id,
          productName: product.product_name,
          rate: product.product_cost,
          quantity: 1,
          amount: 1 * product.product_cost,
          model: product.product_model,
          manufacturerId: product.manufacturer_id,
          manufacturerName: product.manufacturer_name,
          sku: product.sku,
          description: product.product_description,
          systemId: activeSystemId,
          phaseId: activeSystemId ? activePhaseId : null,
        })),
        total: pagination.total,
        offset: pagination.offset,
        nextOffset: pagination.nextOffset,
      };

      return productsData;
    } catch (error) {
      if (
        error.response?.data?.error === "jwt expired" ||
        error.response?.data?.error === "Unauthorized. User not found"
      ) {
        localStorage.removeItem("coyote-token");

        navigate(
          `/${decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )
            ? "admin"
            : "user"
          }/auth/sign-in`,
          {
            state: { status: "expired" },
          }
        );
      }
      return {
        products: [],
        total: -1,
        offset: 0,
        nextOffset: null,
      };
    }
  };

  const handleView = async (
    e,
    item,
    selectedProductIds = [],
    index,
    valuesData
  ) => {
    e.preventDefault();
    setItemView(item);
    setSelectedProductIds(selectedProductIds);
    setItemViewModel(true);
    setItemViewIndex(index);
    setValuesData(valuesData);
  };

  const handleCloseView = async () => {
    setItemUpdateEnabled(false);
    setItemViewModel(false);
  };

  const handleCloseProductsView = async () => {
    setProductsViewModel(false);
  };

  const validationSchemaItem = () =>
    Yup.object().shape({
      productId: Yup.number()
        .integer()
        .required("Product is required")
        .typeError("Product must be a valid"),
      manufacturerId: Yup.number()
        .integer()
        .required("Manufacturer is required")
        .typeError("Manufacturer must be a valid"),
      systemId: Yup.number()
        .integer()
        .required("System is required")
        .typeError("System must be a valid"),
      phaseId: Yup.number()
        .integer()
        .required("Phase is required")
        .typeError("Phase must be a valid"),
      quantity: Yup.number()
        .required("Quantity is required")
        .integer("Quantity must be an integer")
        .typeError("Quantity must be a number")
        .positive("Quantity must be greater than 0"),
      rate: Yup.number()
        .required("Rate is required")
        .typeError("Rate must be a number")
        .moreThan(0, "Rate must be greater than 0")
        .max(9999999999999.99, "Rate cannot exceed 9999999999999.99")
        .transform((value) => (value === "" ? null : value))
        .test(
          "is-decimal",
          "Rate must have a maximum of 2 decimal places",
          (value) => {
            if (value === null) return true;
            const isValid = /^[+-]?\d+(\.\d{1,2})?$/.test(value.toString());
            return isValid;
          }
        )
        .test(
          "max-digits",
          "Rate cannot have more than 13 digits before decimal point",
          (value) => {
            if (value === null) return true;
            const [integerPart] = value.toString().split(".");
            return integerPart.replace("-", "").length <= 13;
          }
        ),
      amount: Yup.number()
        .required("Amount is required")
        .typeError("Amount must be a number"),
    });

  const handleSubmitItem = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    const updatedItems = [...valuesData.items];
    updatedItems[itemViewIndex] = values;
    const updatedValuesData = { ...valuesData, items: updatedItems };
    setFormData(updatedValuesData);
    setItemViewIndex(null);
    setItemViewModel(false);
    setItemUpdateEnabled(false);
    setSubmitting(false);
  };

  const handleSortProductRequest = async (columnKey, setFieldValue, values) => {
    let direction = "asc";

    if (
      sortConfigProductRequest.key === columnKey &&
      sortConfigProductRequest.direction === "asc"
    ) {
      direction = "desc";
    } else if (
      sortConfigProductRequest.key === columnKey &&
      sortConfigProductRequest.direction === "desc"
    ) {
      direction = "asc";
    }
    setSortConfigProductRequest({ key: columnKey, direction });
    setFieldValue(
      "items",
      [...values.items].sort((a, b) => {
        let aValue = a[columnKey]?.toString().toLowerCase();
        let bValue = b[columnKey]?.toString().toLowerCase();

        if (aValue < bValue) return direction === "asc" ? -1 : 1;
        if (aValue > bValue) return direction === "asc" ? 1 : -1;
        return 0;
      })
    );
  };

  const getFilteredPhasesForSystem = (systemId) => {
    if (!systemId) return [];

    const selectedSystem = systems.find((sys) => sys.system_id === systemId);

    if (!selectedSystem || !selectedSystem.phases) return [];

    return selectedSystem.phases.map((phase) => ({
      value: phase.phase_id,
      label: phase.phase_name,
    }));
  };

  const getFilteredItems = (items) => {
    if (
      !searchSelectedProducts.trim() &&
      !selectedSystemForFilter &&
      !selectedPhaseForFilter
    ) {
      return items || [];
    }

    const normalize = (str) => str?.toLowerCase().replace(/[+-]/g, "") || "";

    const searchTerm = normalize(searchSelectedProducts);
    const hasSearchTerm = searchSelectedProducts.trim();
    const hasSystemId = selectedSystemForFilter;
    const hasPhaseId = selectedPhaseForFilter;

    return (items || []).filter((item) => {
      const system =
        systems.find((sys) => sys.system_id === item.systemId) || {};
      const phase = phases.find((ph) => ph.phase_id === item.phaseId) || {};

      const searchableFields = [
        item.productName,
        item.manufacturerName,
        item.model,
        item.description,
        item.sku,
        system.system_name,
        phase.phase_name,
      ].filter(Boolean);

      const combinedText = normalize(searchableFields.join(" ").toLowerCase());
      const tokens = searchTerm.trim().split(/\s+/);
      const searchMatch = tokens.every((token) => combinedText.includes(token));

      const systemIdMatch =
        item.systemId &&
        item.systemId.toString() === selectedSystemForFilter?.value?.toString();

      const phaseIdMatch =
        item.phaseId &&
        item.phaseId.toString() === selectedPhaseForFilter?.value?.toString();

      if (hasSearchTerm && hasSystemId && hasPhaseId) {
        return searchMatch && systemIdMatch && phaseIdMatch;
      }

      if (hasSearchTerm && hasSystemId && !hasPhaseId) {
        return searchMatch && systemIdMatch;
      }

      if (hasSearchTerm && !hasSystemId && hasPhaseId) {
        return searchMatch && phaseIdMatch;
      }

      if (!hasSearchTerm && hasSystemId && hasPhaseId) {
        return systemIdMatch && phaseIdMatch;
      }

      if (hasSearchTerm && !hasSystemId && !hasPhaseId) {
        return searchMatch;
      }

      if (!hasSearchTerm && hasSystemId && !hasPhaseId) {
        return systemIdMatch;
      }

      if (!hasSearchTerm && !hasSystemId && hasPhaseId) {
        return phaseIdMatch;
      }

      return false;
    });
  };

  const getPaginatedItems = (items) => {
    const filteredItems = getFilteredItems(items);
    const itemsToShow = currentPage * itemsPerPage;
    return filteredItems.slice(0, itemsToShow);
  };

  const hasMoreItems = (items) => {
    const filteredItems = getFilteredItems(items);
    const itemsToShow = currentPage * itemsPerPage;
    return filteredItems.length > itemsToShow;
  };

  const loadMoreItems = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const handleAddProducts = async (newProducts) => {
    setIsMerging(true);

    await new Promise((resolve) => setTimeout(resolve, 500));

    const mergedProducts = mergeProducts(selectedAddedProducts, newProducts);
    setSelectedAddedProducts(mergedProducts);

    setIsMerging(false);
  };

  const [pendingNavigation, setPendingNavigation] = useState(null);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      setPendingNavigation(blocker.location);
      setShowDiscardModal(true);
    }
  }, [blocker]);

  return (
    <>
      <div>
        <div className="bd-example">
          <div className="d-flex justify-content-between flex-wrap pl-0 pt-0">
            {/* Discard Modal */}
            <Modal
              show={showDiscardModal}
              onHide={() => {
                setShowDiscardModal(false);
                setPendingNavigation(null);
                if (blocker.state === "blocked") {
                  blocker.reset();
                }
              }}
              backdrop="static"
              keyboard={false}
              centered
            >
              <Modal.Header className="custom-modal-header d-flex justify-content-center items-center relative">
                <Modal.Title>Unsaved Changes</Modal.Title>
                <button
                  type="button"
                  className="btn-close-svg"
                  style={{ position: "absolute", right: "1rem" }}
                  onClick={() => {
                    setShowDiscardModal(false);
                    setPendingNavigation(null);
                    if (blocker.state === "blocked") {
                      blocker.reset();
                    }
                  }}
                  aria-label="Close"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </Modal.Header>
              <Modal.Body className="text-center px-4 pt-2">
                <p
                  className="text-muted mb-4 px-2"
                  style={{ fontSize: "1rem", lineHeight: "1.5" }}
                >
                  You have unsaved products added. Do you want to discard these
                  changes?
                </p>
              </Modal.Body>
              <Modal.Footer className="border-0 justify-content-end pb-4 pt-0">
                <Button
                  type="button"
                  className="btn btn-white me-4"
                  variant="btn btn-white"
                  onClick={() => {
                    setHasUnsavedChanges(false);
                    setShowDiscardModal(false);

                    if (pendingNavigation) {
                      if (blocker.state === "blocked") {
                        blocker.proceed();
                      }
                      setPendingNavigation(null);
                    } else {
                      navigate(`/dashboard/app/job-edit/${jobId}`);
                    }
                  }}
                  style={{
                    borderRadius: "25px",
                    fontWeight: "500",
                    minWidth: "100px",
                    borderColor: "#6c757d",
                    color: "#6c757d",
                  }}
                >
                  Discard
                </Button>
                <Button
                  className="px-4 py-2"
                  style={{
                    backgroundColor: "#3a57e8",
                    borderColor: "#3a57e8",
                    borderRadius: "25px",
                    fontWeight: "500",
                    minWidth: "100px",
                  }}
                  onClick={() => {
                    setShowDiscardModal(false);
                    setPendingNavigation(null);
                    if (blocker.state === "blocked") {
                      blocker.reset();
                    }
                  }}
                >
                  Continue
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Item View Modal */}
            <Modal
              show={itemViewModel}
              onHide={handleCloseView}
              backdrop="true"
              keyboard={true}
              size="lg"
            >
              <Modal.Header className="custom-modal-header">
                <Modal.Title>View Item</Modal.Title>
                <button
                  type="button"
                  className="btn-close-svg"
                  onClick={handleCloseView}
                  aria-label="Close"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </Modal.Header>
              <Formik
                initialValues={itemView}
                validationSchema={validationSchemaItem}
                onSubmit={handleSubmitItem}
                enableReinitialize
              >
                {({
                  values,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                  handleSubmit,
                  isSubmitting,
                }) => (
                  <>
                    <Form
                      className="form-horizontal ps-3 pe-3"
                      onSubmit={handleSubmit}
                    >
                      <FocusError />
                      <Modal.Body>
                        <Row>
                          <Col sm="6">
                            <Form.Group className="form-group">
                              <Form.Label
                                htmlFor="ProductId"
                                column
                                sm="4"
                                className="control-label align-self-center mb-0 "
                              >
                                Product Name
                              </Form.Label>
                              <div title={values.productName || ""}>
                                <Form.Control
                                  readOnly
                                  type="text"
                                  id={`productName`}
                                  name={`productName`}
                                  value={values.productName || ""}
                                  style={{
                                    fontSize: "0.8rem",
                                    cursor: "not-allowed",
                                  }}
                                />
                              </div>
                            </Form.Group>
                          </Col>
                          <Col sm="6">
                            <Form.Group className="form-group">
                              <Form.Label
                                htmlFor="manufacturerId"
                                column
                                sm="4"
                                className="control-label align-self-center mb-0"
                              >
                                Make
                              </Form.Label>
                              <div
                                style={{
                                  position: "relative",
                                }}
                                title={values.manufacturerName || ""}
                              >
                                <Form.Control
                                  readOnly
                                  type="text"
                                  id={`manufacturerName`}
                                  name={`manufacturerName`}
                                  value={values.manufacturerName || ""}
                                  style={{
                                    fontSize: "0.8rem",
                                    cursor: "not-allowed",
                                  }}
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm="6">
                            <Form.Group className="form-group">
                              <Form.Label
                                htmlFor="model"
                                column
                                sm="4"
                                className="control-label align-self-center mb-0"
                              >
                                Model
                              </Form.Label>
                              <div
                                style={{
                                  position: "relative",
                                }}
                                title={values.model || ""}
                              >
                                <Form.Control
                                  readOnly
                                  type="text"
                                  id={`model`}
                                  name={`model`}
                                  value={values.model || ""}
                                  style={{
                                    fontSize: "0.8rem",
                                    width: "100%",
                                    cursor: "not-allowed",
                                  }}
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm={12}>
                            <Form.Group className="form-group">
                              <Form.Label
                                htmlFor="description"
                                column
                                sm="12"
                                className="control-label align-self-center mb-0"
                              >
                                Description
                              </Form.Label>
                              <div
                                style={{
                                  position: "relative",
                                }}
                              >
                                <Form.Control
                                  readOnly
                                  type="textarea"
                                  id={`description`}
                                  as="textarea"
                                  name={`description`}
                                  value={values.description}
                                  style={{
                                    resize: "none",
                                    width: "100%",
                                    fontSize: "0.8rem",
                                    cursor: "not-allowed",
                                  }}
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm="6">
                            <Form.Group className="form-group">
                              <Form.Label
                                htmlFor="quantity"
                                column
                                sm="4"
                                className="control-label align-self-center mb-0"
                              >
                                Quantity
                              </Form.Label>
                              <div
                                style={{
                                  position: "relative",
                                }}
                              >
                                <Form.Control
                                  type="text"
                                  name={`quantity`}
                                  step="1"
                                  value={values.quantity}
                                  min={0}
                                  readOnly
                                  style={{
                                    fontSize: "0.8rem",
                                    cursor: "not-allowed",
                                    width: "100%",
                                  }}
                                />
                              </div>
                            </Form.Group>
                          </Col>

                          <Col sm={6}>
                            <Form.Group className="form-group">
                              <Form.Label
                                htmlFor="rate"
                                column
                                sm="6"
                                className="control-label align-self-center mb-0"
                              >
                                Rate
                              </Form.Label>
                              <div
                                style={{
                                  position: "relative",
                                }}
                              >
                                <Form.Control
                                  type="text"
                                  step="0.01"
                                  name={`rate`}
                                  min={0}
                                  value={values.rate || ""}
                                  readOnly
                                  style={{
                                    fontSize: "0.8rem",
                                    cursor: "not-allowed",
                                  }}
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col sm={6}>
                            <Form.Group className="form-group">
                              <Form.Label
                                htmlFor="amount"
                                column
                                sm="4"
                                className="control-label align-self-center mb-0"
                              >
                                Amount
                              </Form.Label>
                              <div
                                style={{
                                  position: "relative",
                                }}
                              >
                                <Form.Control
                                  type="text"
                                  step="0.01"
                                  pattern="^\d+(\.\d{0,2})?$"
                                  name={`amount`}
                                  min={0}
                                  value={values.amount || 0}
                                  readOnly
                                  style={{
                                    fontSize: "0.8rem",
                                    cursor: "not-allowed",
                                  }}
                                />
                              </div>
                            </Form.Group>
                          </Col>

                          <Col sm={6}>
                            <Form.Group className="form-group">
                              <Form.Label
                                htmlFor="systemId"
                                column
                                sm="4"
                                className="control-label align-self-center mb-0"
                              >
                                System
                              </Form.Label>
                              <div
                                title={
                                  values.systemId
                                    ? optionsSystem.find(
                                      (option) =>
                                        option.value === values.systemId
                                    )?.label
                                    : ""
                                }
                              >
                                <Select
                                  name={`systemId`}
                                  options={optionsSystem}
                                  value={optionsSystem.find(
                                    (option) => option.value === values.systemId
                                  )}
                                  isDisabled={!itemUpdateEnabled}
                                  isClearable
                                  placeholder="N/A"
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  menuPlacement="auto"
                                  styles={{
                                    container: (provided) => ({
                                      ...provided,
                                      width: "100%",
                                    }),
                                    option: (provided, state) => ({
                                      ...provided,
                                      backgroundColor: state.isSelected
                                        ? "#007bff"
                                        : state.isFocused
                                          ? "#e6f7ff"
                                          : "#fff",
                                      color: "#333",
                                      fontSize: "0.8rem",
                                      cursor: "not-allowed",
                                    }),
                                    menuPortal: (provided) => ({
                                      ...provided,
                                      zIndex: 9999,
                                    }),
                                    menu: (provided) => ({
                                      ...provided,
                                      zIndex: 9999,
                                    }),
                                    control: (provided) => ({
                                      ...provided,
                                      color: "#5e667b",
                                    }),
                                    placeholder: (provided) => ({
                                      ...provided,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }),
                                  }}
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm={6}>
                            <Form.Group className="form-group">
                              <Form.Label
                                htmlFor="phaseId"
                                column
                                sm="4"
                                className="control-label align-self-center mb-0"
                              >
                                Phase
                              </Form.Label>
                              <div
                                title={
                                  values.phaseId
                                    ? getFilteredPhasesForSystem(
                                      values.systemId
                                    ).find(
                                      (option) =>
                                        option.value === values.phaseId
                                    )?.label
                                    : ""
                                }
                              >
                                <Select
                                  name={`phaseId`}
                                  options={getFilteredPhasesForSystem(
                                    values.systemId
                                  )}
                                  value={getFilteredPhasesForSystem(
                                    values.systemId
                                  ).find(
                                    (option) => option.value === values.phaseId
                                  )}
                                  isDisabled={!itemUpdateEnabled}
                                  isClearable
                                  placeholder="N/A"
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  menuPlacement="auto"
                                  styles={{
                                    container: (provided) => ({
                                      ...provided,
                                      width: "100%",
                                    }),
                                    option: (provided, state) => ({
                                      ...provided,
                                      backgroundColor: state.isSelected
                                        ? "#007bff"
                                        : state.isFocused
                                          ? "#e6f7ff"
                                          : "#fff",
                                      color: "#333",
                                      fontSize: "0.8rem",
                                      cursor: "not-allowed",
                                    }),
                                    menuPortal: (provided) => ({
                                      ...provided,
                                      zIndex: 9999,
                                    }),
                                    menu: (provided) => ({
                                      ...provided,
                                      zIndex: 9999,
                                    }),
                                    control: (provided) => ({
                                      ...provided,
                                      color: "#5e667b",
                                    }),
                                    placeholder: (provided) => ({
                                      ...provided,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }),
                                  }}
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>
                      </Modal.Body>
                    </Form>
                  </>
                )}
              </Formik>
            </Modal>

            {/* Add Products Modal */}
            <AddProductsModal
              show={productsViewModel}
              onHide={handleCloseProductsView}
              onAddProducts={handleAddProducts}
              systems={systems}
              phases={phases}
              optionsSystem={optionsSystem}
              fetchProductSuggestions={fetchProductSuggestions}
              selectedProductListIds={selectedProductListIds}
              activeSystemId={activeSystemId}
              activePhaseId={activePhaseId}
              token={token}
            />
          </div>
        </div>

        <Formik
          enableReinitialize={true}
          initialValues={{
            ...formData,
            warehouseId: warehouseId,
            items: selectedAddedProducts,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            handleChange,
            handleBlur,
            touched,
            setFieldValue,
            handleSubmit,
            isSubmitting,
            resetForm,
            errors: errorsFormik,
          }) => {
            return (
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  setClickSubmitProduct(true);
                  handleSubmit();
                }}
              >
                <FocusError />
                <Row>
                  <Col xl="12" lg="12" ref={alertRef}>
                    <Card>
                      <Card.Header className="d-flex justify-content-between">
                        <div
                          className="breadcrumb header-title"
                          style={{ cursor: "pointer" }}
                        >
                          <h4
                            onClick={() => {
                              if (hasUnsavedChanges) {
                                setPendingNavigation({
                                  pathname: "/dashboard/app/job-list",
                                });
                                setShowDiscardModal(true);
                              } else {
                                navigate("/dashboard/app/job-list");
                              }
                            }}
                            className="breadcrumbLink"
                          >
                            Job
                          </h4>
                          <h4 className="mx-2 card-title">{">"}</h4>
                          <h4
                            onClick={() =>
                              navigate(`/dashboard/app/job-edit/${jobId}`)
                            }
                            className="text-primary card-title"
                            style={{ cursor: "pointer" }}
                          >
                            <span>Bill of Materials</span>
                          </h4>
                          <h4 className="mx-2 card-title">{">"}</h4>
                          <h4 className="card-title">Add</h4>
                        </div>
                        <div></div>
                      </Card.Header>
                      <Card.Body>
                        <AlertNotificationSimple
                          type={alert.type}
                          message={alert.message}
                          show={alert.show}
                          onClose={() => setAlert({ ...alert, show: false })}
                        />
                        <Row>
                          <Col sm={4} lg={3} className=" mb-2">
                            <Form.Group className="">
                              <Form.Label>
                                Receiving Warehouse{" "}
                                <span className="text-danger">*</span>{" "}
                              </Form.Label>
                              <Select
                                closeMenuOnScroll={(e) => {
                                  try {
                                    const target = e.target;

                                    if (
                                      !target ||
                                      !target.nodeType ||
                                      typeof target.closest !== "function"
                                    ) {
                                      return true;
                                    }

                                    const isMenuScroll =
                                      target.closest('[class*="MenuList"]') ||
                                      target.closest('[class*="menu"]');

                                    return !isMenuScroll;
                                  } catch (error) {
                                    return true;
                                  }
                                }}
                                options={warehouseOptions}
                                value={warehouseOptions.find(
                                  (option) => option.value === warehouseId
                                )}
                                onChange={(option) => {
                                  const selectedWarehouseId =
                                    option?.value || null;
                                  setWarehouseId(selectedWarehouseId);
                                  setFieldValue(
                                    "warehouseId",
                                    selectedWarehouseId
                                  );
                                  setWarehouseAddress(option?.shipTo || "");
                                }}
                                placeholder={
                                  warehouseOptions.length === 0 ? (
                                    <span>No warehouse found</span>
                                  ) : (
                                    "Select Warehouse..."
                                  )
                                }
                                className="display-content-dropdown"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuPlacement="auto"
                                noOptionsMessage={() => (
                                  <div
                                    style={{
                                      textAlign: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <span style={{ color: "#666" }}>
                                      No warehouse found.
                                      {(warehouseHasCreatePermission ||
                                        decodedToken?.roles.some(
                                          (role) =>
                                            role.role_type ===
                                            roleTypes.SUPER_ADMIN
                                        )) && (
                                          <span>
                                            {" "}
                                            <Link
                                              to="/dashboard/app/warehouse-add"
                                              style={{
                                                color: "#007bff",
                                                textDecoration: "none",
                                                cursor: "pointer",
                                              }}
                                            >
                                              Add new
                                            </Link>
                                          </span>
                                        )}
                                    </span>
                                  </div>
                                )}
                                styles={{
                                  option: (provided, state) => ({
                                    ...provided,
                                    backgroundColor: state.isSelected
                                      ? "#007bff"
                                      : state.isFocused
                                        ? "#e6f7ff"
                                        : "#fff",
                                    color: "#333",
                                  }),
                                  control: (provided) => ({
                                    ...provided,
                                    color: "#5e667b",
                                    width: "100%",
                                    marginTop: "0.2rem",
                                  }),
                                  menuPortal: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                  }),
                                  menu: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                  }),
                                  noOptionsMessage: (provided) => ({
                                    ...provided,
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }),
                                }}
                              />

                              <ErrorMessage
                                name="warehouseId"
                                component="div"
                                className="text-danger"
                              />

                              {errors?.warehouseId && (
                                <span className="text-danger">
                                  {errors.warehouseId}
                                </span>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        {!loader ? (
                          <>
                            <Row className="align-items-end gap-6">
                              <Col sm={4} lg={3}>
                                <Form.Group className="mt-4">
                                  <Form.Label className="w-100 mb-0">
                                    Search
                                    <span
                                      className="d-flex flex-col"
                                      style={{
                                        width: "100%",
                                        display: "inline-flex",
                                      }}
                                    ></span>
                                    <Form.Control
                                      type="text"
                                      name="name"
                                      className="w-100"
                                      placeholder="Search product"
                                      value={searchSelectedProducts}
                                      onChange={async (e) => {
                                        const inputValue = e.target.value;
                                        setSearchSelectedProducts(inputValue);
                                      }}
                                      onBlur={handleBlur}
                                      style={{
                                        paddingTop: "7px",
                                        paddingBottom: "7px",
                                      }}
                                    />
                                  </Form.Label>
                                </Form.Group>
                              </Col>
                              <Col sm={4} lg={3}>
                                <div
                                  title={
                                    selectedSystemForFilter
                                      ? selectedSystemForFilter.label
                                      : ""
                                  }
                                  className="align-items-center flex-end flex mt-4"
                                >
                                  <Select
                                    closeMenuOnScroll={(e) => {
                                      try {
                                        const target = e.target;

                                        if (
                                          !target ||
                                          !target.nodeType ||
                                          typeof target.closest !== "function"
                                        ) {
                                          return true;
                                        }

                                        const isMenuScroll =
                                          target.closest(
                                            '[class*="MenuList"]'
                                          ) ||
                                          target.closest('[class*="menu"]');

                                        return !isMenuScroll;
                                      } catch (error) {
                                        return true;
                                      }
                                    }}
                                    name={`systemIdFilter`}
                                    options={optionsSystem}
                                    value={selectedSystemForFilter}
                                    onChange={(selectedOption) => {
                                      setSelectedSystemForFilter(
                                        selectedOption || null
                                      );
                                      if (!selectedOption) {
                                        setSelectedPhaseForFilter(null);
                                      }
                                    }}
                                    isClearable
                                    placeholder={
                                      selectedSystemForFilter &&
                                        optionsSystem.length === 0
                                        ? "No data found"
                                        : "Select System"
                                    }
                                    noOptionsMessage={() => "No data found"}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    menuPlacement="auto"
                                    styles={{
                                      container: (provided) => ({
                                        ...provided,
                                        width: "100%",
                                      }),
                                      option: (provided, state) => ({
                                        ...provided,
                                        backgroundColor: state.isSelected
                                          ? "#007bff"
                                          : state.isFocused
                                            ? "#e6f7ff"
                                            : "#fff",
                                        color: "#333",
                                        fontSize: "0.8rem",
                                      }),
                                      menuPortal: (provided) => ({
                                        ...provided,
                                        zIndex: 9999,
                                      }),
                                      menu: (provided) => ({
                                        ...provided,
                                        zIndex: 9999,
                                      }),
                                      control: (provided) => ({
                                        ...provided,
                                        color: "#5e667b",
                                      }),
                                      placeholder: (provided) => ({
                                        ...provided,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }),
                                    }}
                                  />
                                </div>
                              </Col>
                              <Col sm={4} lg={3}>
                                <div
                                  className="mt-4"
                                  title={
                                    selectedPhaseForFilter
                                      ? selectedPhaseForFilter.label
                                      : ""
                                  }
                                >
                                  <Select
                                    closeMenuOnScroll={(e) => {
                                      try {
                                        const target = e.target;

                                        if (
                                          !target ||
                                          !target.nodeType ||
                                          typeof target.closest !== "function"
                                        ) {
                                          return true;
                                        }

                                        const isMenuScroll =
                                          target.closest(
                                            '[class*="MenuList"]'
                                          ) ||
                                          target.closest('[class*="menu"]');

                                        return !isMenuScroll;
                                      } catch (error) {
                                        return true;
                                      }
                                    }}
                                    name={`phaseIdFilter`}
                                    options={
                                      selectedSystemForFilter
                                        ? getFilteredPhasesForSystem(
                                          selectedSystemForFilter.value
                                        )
                                        : []
                                    }
                                    value={
                                      selectedSystemForFilter &&
                                        selectedPhaseForFilter
                                        ? selectedPhaseForFilter
                                        : null
                                    }
                                    onChange={(selectedOption) => {
                                      setSelectedPhaseForFilter(
                                        selectedOption || null
                                      );
                                    }}
                                    isClearable
                                    isDisabled={!selectedSystemForFilter}
                                    placeholder={
                                      !selectedSystemForFilter
                                        ? "First select a system"
                                        : getFilteredPhasesForSystem(
                                          selectedSystemForFilter.value
                                        ).length === 0
                                          ? "No data found"
                                          : "Select phase"
                                    }
                                    noOptionsMessage={() =>
                                      !selectedSystemForFilter
                                        ? "First select a system"
                                        : "No data found"
                                    }
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    menuPlacement="auto"
                                    styles={{
                                      container: (provided) => ({
                                        ...provided,
                                        width: "100%",
                                      }),
                                      option: (provided, state) => ({
                                        ...provided,
                                        backgroundColor: state.isSelected
                                          ? "#007bff"
                                          : state.isFocused
                                            ? "#e6f7ff"
                                            : "#fff",
                                        color: "#333",
                                        fontSize: "0.8rem",
                                      }),
                                      menuPortal: (provided) => ({
                                        ...provided,
                                        zIndex: 9999,
                                      }),
                                      menu: (provided) => ({
                                        ...provided,
                                        zIndex: 9999,
                                      }),
                                      control: (provided) => ({
                                        ...provided,
                                        color: "#5e667b",
                                        backgroundColor:
                                          !selectedSystemForFilter
                                            ? "#e9ecef"
                                            : "#fff",
                                      }),
                                      placeholder: (provided) => ({
                                        ...provided,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }),
                                    }}
                                  />
                                </div>
                              </Col>
                              <Col
                                sm={12}
                                lg={3}
                                className="align-items-end justify-content-end text-end mt-4"
                              >
                                <Button
                                  variant="primary"
                                  className="text-nowrap mr-4"
                                  onClick={async () => {
                                    setSelectedProductListIds([]);
                                    setSelectedAddedProducts(values.items);
                                    setProductsViewModel(true);
                                    setSearchSelectedProducts("");
                                    setSelectedSystemForFilter(null);
                                    setSelectedPhaseForFilter(null);
                                  }}
                                >
                                  Add Products
                                </Button>
                              </Col>
                            </Row>
                            <div
                              className="table-responsive relative mt-4"
                              style={{ position: "relative" }}
                            >
                              {isMerging && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    zIndex: 1000,
                                  }}
                                >
                                  <div className="d-flex flex-column align-items-center">
                                    <Spinner
                                      animation="border"
                                      role="status"
                                      variant="primary"
                                      size="lg"
                                      style={{
                                        width: "3rem",
                                        height: "3rem",
                                      }}
                                    />
                                    <span
                                      className="mt-3 text-primary"
                                      style={{
                                        fontSize: "1rem",
                                        fontWeight: "500",
                                      }}
                                    >
                                      Processing products...
                                    </span>
                                  </div>
                                </div>
                              )}

                              {((typeof errorsFormik?.items === "string" &&
                                selectedProducts.length === 0 &&
                                clickSubmitProduct) ||
                                (typeof errorsFormik?.items === "string" &&
                                  values?.items?.length === 0 &&
                                  selectedProducts?.length === 0 &&
                                  clickSubmitProduct) ||
                                (typeof errorsFormik?.items === "string" &&
                                  values?.items?.length === 0 &&
                                  clickSubmitProduct)) && (
                                  <span className="text-danger">
                                    {errorsFormik.items}
                                  </span>
                                )}

                              <Table
                                id="product-request-list-table datatable"
                                className="overflow-auto"
                                role="grid"
                                responsive
                                data-toggle="data-table"
                                style={{
                                  width: "100%",
                                  opacity: isMerging ? 0.5 : 1,
                                  transition: "opacity 0.3s ease",
                                }}
                              >
                                <thead>
                                  <tr style={{ backgroundColor: "#e2e4e9" }}>
                                    <th
                                      className=""
                                      onClick={() =>
                                        handleSortProductRequest(
                                          "productName",
                                          setFieldValue,
                                          values
                                        )
                                      }
                                      style={{ cursor: "pointer" }}
                                    >
                                      <div className="d-flex flex-row align-items-end user-select-none">
                                        Product Name{" "}
                                        <span className="d-flex flex-column ms-2">
                                          <BsCaretUpFill
                                            color={
                                              sortConfigProductRequest.key ===
                                                "productName" &&
                                                sortConfigProductRequest.direction ===
                                                "asc"
                                                ? "#3a57e8"
                                                : "#ccc"
                                            }
                                          />
                                          <BsCaretDownFill
                                            color={
                                              sortConfigProductRequest.key ===
                                                "productName" &&
                                                sortConfigProductRequest.direction ===
                                                "desc"
                                                ? "#3a57e8"
                                                : "#ccc"
                                            }
                                          />
                                        </span>
                                      </div>
                                    </th>
                                    <th style={{ minWidth: "100px" }}>
                                      Quantity
                                    </th>
                                    <th
                                      className="user-select-none"
                                      onClick={() =>
                                        handleSortProductRequest(
                                          "manufacturerName",
                                          setFieldValue,
                                          values
                                        )
                                      }
                                      style={{ cursor: "pointer" }}
                                    >
                                      <div className="d-flex flex-row  align-items-end">
                                        Make{" "}
                                        <span className="d-flex flex-column ms-2">
                                          <BsCaretUpFill
                                            color={
                                              sortConfigProductRequest.key ===
                                                "manufacturerName" &&
                                                sortConfigProductRequest.direction ===
                                                "asc"
                                                ? "#3a57e8"
                                                : "#ccc"
                                            }
                                          />
                                          <BsCaretDownFill
                                            color={
                                              sortConfigProductRequest.key ===
                                                "manufacturerName" &&
                                                sortConfigProductRequest.direction ===
                                                "desc"
                                                ? "#3a57e8"
                                                : "#ccc"
                                            }
                                          />
                                        </span>
                                      </div>
                                    </th>
                                    <th
                                      className="user-select-none"
                                      onClick={() =>
                                        handleSortProductRequest(
                                          "model",
                                          setFieldValue,
                                          values
                                        )
                                      }
                                      style={{ cursor: "pointer" }}
                                    >
                                      <div className="d-flex flex-row align-items-end ">
                                        Model{" "}
                                        <span className="d-flex flex-column ms-2">
                                          <BsCaretUpFill
                                            color={
                                              sortConfigProductRequest.key ===
                                                "model" &&
                                                sortConfigProductRequest.direction ===
                                                "asc"
                                                ? "#3a57e8"
                                                : "#ccc"
                                            }
                                          />
                                          <BsCaretDownFill
                                            color={
                                              sortConfigProductRequest.key ===
                                                "model" &&
                                                sortConfigProductRequest.direction ===
                                                "desc"
                                                ? "#3a57e8"
                                                : "#ccc"
                                            }
                                          />
                                        </span>
                                      </div>
                                    </th>
                                    <th style={{ minWidth: "200px" }}>SKU</th>
                                
                                    <th style={{ minWidth: "200px" }}>Rate</th>
                                    <th style={{ minWidth: "200px" }}>
                                      Amount
                                    </th>
                                    <th style={{ minWidth: "200px" }}>
                                      System
                                    </th>
                                    <th style={{ minWidth: "200px" }}>Phase</th>
                                    <th style={{ minWidth: "100px" }}>
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <FieldArray
                                    name="items"
                                    render={(arrayHelpers) => {
                                      if (values.items.length <= itemsPerPage) {
                                        setCurrentPage(1);
                                      }

                                      const paginatedItems = getPaginatedItems(
                                        values.items
                                      );
                                      const showLoadMore = hasMoreItems(
                                        values.items
                                      );
                                      return (
                                        <>
                                          {paginatedItems?.map(
                                            (item, index) => {
                                              const originalIndex =
                                                values.items.findIndex(
                                                  (originalItem) =>
                                                    originalItem.productId ===
                                                    item.productId &&
                                                    originalItem.systemId ===
                                                    item.systemId &&
                                                    originalItem.phaseId ===
                                                    item.phaseId
                                                );

                                              const uniqueRowKey = `${item.productId
                                                }-${item.systemId || "null"}-${item.phaseId || "null"
                                                }`;

                                              const formikErrors =
                                                errorsFormik?.items?.[
                                                originalIndex
                                                ];
                                              const touchedFields =
                                                touched?.items?.[originalIndex];
                                              const generalErrors =
                                                errors?.items?.[originalIndex];
                                              const backendProductErrors =
                                                backendValidationErrors.find(
                                                  (detail) =>
                                                    detail.productId ===
                                                    item.productId &&
                                                    detail.systemId ===
                                                    item.systemId &&
                                                    detail.phaseId ===
                                                    item.phaseId
                                                );
                                              const hasValidationErrors =
                                                formikErrors && touchedFields;
                                              const hasGeneralErrors =
                                                generalErrors;
                                              const hasBackendErrors =
                                                backendProductErrors &&
                                                backendProductErrors.errors
                                                  .length > 0;
                                              const shouldShowBorder =
                                                hasValidationErrors ||
                                                hasBackendErrors ||
                                                hasGeneralErrors;

                                              return (
                                                <tr
                                                  key={uniqueRowKey}
                                                  className={
                                                    (hasValidationErrors &&
                                                      "error-row bg-selected-product") ||
                                                    (hasGeneralErrors &&
                                                      "bg-selected-product") ||
                                                    (hasBackendErrors &&
                                                      "error-row bg-selected-product") ||
                                                    (inactiveProductErrors.includes(
                                                      item.productId
                                                    ) &&
                                                      "error-row bg-selected-product") ||
                                                    "bg-selected-product"
                                                  }
                                                  style={{
                                                    ...((shouldShowBorder &&
                                                      !inactiveProductErrors.includes(
                                                        item.productId
                                                      )) ||
                                                      (!shouldShowBorder &&
                                                        inactiveProductErrors.includes(
                                                          item.productId
                                                        ))
                                                      ? {
                                                        border:
                                                          "2px solid red",
                                                      }
                                                      : {}),
                                                  }}
                                                >
                                                  <td>
                                                    <Form.Control
                                                      readOnly
                                                      type="text"
                                                      id={`values.items${originalIndex}.productName`}
                                                      name={`values.items${originalIndex}.productName`}
                                                      value={
                                                        item.productName || ""
                                                      }
                                                      style={{
                                                        width: "200px",
                                                        fontSize: "0.8rem",
                                                        cursor: "not-allowed",
                                                      }}
                                                    />
                                                    <ErrorMessage
                                                      name={`items.${originalIndex}.productId`}
                                                      component="div"
                                                      className="text-danger"
                                                    />
                                                    {errors?.items?.[
                                                      originalIndex
                                                    ]?.productId && (
                                                        <span>
                                                          {
                                                            errors?.items[
                                                              originalIndex
                                                            ]?.productId
                                                          }
                                                          <br />
                                                        </span>
                                                      )}
                                                    {backendValidationErrors
                                                      .find(
                                                        (detail) =>
                                                          detail.productId ===
                                                          item.productId
                                                      )
                                                      ?.errors?.filter(
                                                        (error) =>
                                                          error.id ===
                                                          item.productId
                                                      )
                                                      .map((error, idx) => (
                                                        <span
                                                          key={idx}
                                                          className="text-danger"
                                                          style={{
                                                            fontSize: "0.75rem",
                                                          }}
                                                        >
                                                          {error.message}
                                                          <br />
                                                        </span>
                                                      ))}
                                                  </td>
                                                  <td>
                                                    <Form.Control
                                                      type="text"
                                                      name={`items.${originalIndex}.quantity`}
                                                      step="1"
                                                      min={0}
                                                      max={99999}
                                                      value={
                                                        item.quantity || ""
                                                      }
                                                      onChange={(e) => {
                                                        const value =
                                                          e.target.value;

                                                        if (value === "") {
                                                          setFieldValue(
                                                            `items.${originalIndex}.quantity`,
                                                            ""
                                                          );
                                                          setFieldValue(
                                                            `items.${originalIndex}.amount`,
                                                            ""
                                                          );
                                                          setSelectedAddedProducts(
                                                            (prevProducts) => {
                                                              const updated = [
                                                                ...prevProducts,
                                                              ];
                                                              const targetIndex =
                                                                updated.findIndex(
                                                                  (p) =>
                                                                    p.productId ===
                                                                    item.productId &&
                                                                    p.systemId ===
                                                                    item.systemId &&
                                                                    p.phaseId ===
                                                                    item.phaseId
                                                                );
                                                              if (
                                                                targetIndex !==
                                                                -1
                                                              ) {
                                                                updated[
                                                                  targetIndex
                                                                ] = {
                                                                  ...updated[
                                                                  targetIndex
                                                                  ],
                                                                  quantity: "",
                                                                  amount: "",
                                                                };
                                                              }
                                                              return updated;
                                                            }
                                                          );
                                                          return;
                                                        }

                                                        if (
                                                          !/^\d+$/.test(value)
                                                        ) {
                                                          return;
                                                        }

                                                        const parsedValue =
                                                          parseInt(value, 10);

                                                        if (
                                                          parsedValue > 99999
                                                        ) {
                                                          return;
                                                        }

                                                        if (
                                                          !isNaN(parsedValue) &&
                                                          parsedValue.toString() ===
                                                          value &&
                                                          parsedValue >= 0
                                                        ) {
                                                          setFieldValue(
                                                            `items.${originalIndex}.quantity`,
                                                            parsedValue
                                                          );

                                                          const updatedAmount =
                                                            (
                                                              parsedValue *
                                                              (item.rate ?? 0)
                                                            ).toFixed(2);
                                                          setFieldValue(
                                                            `items.${originalIndex}.amount`,
                                                            updatedAmount
                                                          );
                                                          setSelectedAddedProducts(
                                                            (prevProducts) => {
                                                              const updated = [
                                                                ...prevProducts,
                                                              ];
                                                              const targetIndex =
                                                                updated.findIndex(
                                                                  (p) =>
                                                                    p.productId ===
                                                                    item.productId &&
                                                                    p.systemId ===
                                                                    item.systemId &&
                                                                    p.phaseId ===
                                                                    item.phaseId
                                                                );
                                                              if (
                                                                targetIndex !==
                                                                -1
                                                              ) {
                                                                updated[
                                                                  targetIndex
                                                                ] = {
                                                                  ...updated[
                                                                  targetIndex
                                                                  ],
                                                                  quantity:
                                                                    parsedValue,
                                                                  amount:
                                                                    parseFloat(
                                                                      updatedAmount
                                                                    ),
                                                                };
                                                              }
                                                              return updated;
                                                            }
                                                          );
                                                        }
                                                      }}
                                                      style={{
                                                        width: "100px",
                                                        fontSize: "0.8rem",
                                                      }}
                                                    />

                                                    <ErrorMessage
                                                      name={`items.${originalIndex}.quantity`}
                                                      component="div"
                                                      className="text-danger"
                                                    />
                                                    {errors?.items?.[
                                                      originalIndex
                                                    ]?.quantity && (
                                                        <span>
                                                          {
                                                            errors?.items[
                                                              originalIndex
                                                            ]?.quantity
                                                          }
                                                          <br />
                                                        </span>
                                                      )}
                                                  </td>
                                                  <td>
                                                    <div
                                                      title={
                                                        values.items[
                                                          originalIndex
                                                        ].manufacturerName || ""
                                                      }
                                                    >
                                                      <Form.Control
                                                        readOnly
                                                        type="text"
                                                        id={`items.${originalIndex}.manufacturerName`}
                                                        name={`items.${originalIndex}.manufacturerName`}
                                                        value={
                                                          item.manufacturerName ||
                                                          ""
                                                        }
                                                        style={{
                                                          width: "200px",
                                                          fontSize: "0.8rem",
                                                          cursor: "not-allowed",
                                                        }}
                                                      />

                                                      <Form.Control
                                                        type="hidden"
                                                        name={`items.${originalIndex}.manufacturerId`}
                                                        value={
                                                          item.manufacturerId ||
                                                          ""
                                                        }
                                                      />
                                                    </div>
                                                  </td>
                                                  <td>
                                                    <Form.Control
                                                      readOnly
                                                      type="text"
                                                      id={`items.${originalIndex}.model`}
                                                      name={`items.${originalIndex}.model`}
                                                      value={item.model}
                                                      style={{
                                                        resize: "none",
                                                        width: "auto",
                                                        fontSize: "0.8rem",
                                                        cursor: "not-allowed",
                                                      }}
                                                    />
                                                  </td>
                                                  <td>
                                                    <Form.Control
                                                      readOnly
                                                      type="text"
                                                      id={`items.${originalIndex}.sku`}
                                                      name={`items.${originalIndex}.sku`}
                                                      value={item.sku}
                                                      style={{
                                                        resize: "none",
                                                        width: "auto",
                                                        fontSize: "0.8rem",
                                                        cursor: "not-allowed",
                                                      }}
                                                    />
                                                  </td>
                                                  
                                                  <td>
                                                    <Form.Control
                                                      type="text"
                                                      step="0.01"
                                                      min={0}
                                                      name={`items.${originalIndex}.rate`}
                                                      value={item.rate ?? ""}
                                                      onChange={(e) => {
                                                        const inputValue =
                                                          e.target.value;

                                                        if (inputValue === "") {
                                                          setFieldValue(
                                                            `items.${originalIndex}.rate`,
                                                            ""
                                                          );
                                                          setFieldValue(
                                                            `items.${originalIndex}.amount`,
                                                            "0.00"
                                                          );
                                                          setSelectedAddedProducts(
                                                            (prevProducts) => {
                                                              const updated = [
                                                                ...prevProducts,
                                                              ];
                                                              const targetIndex =
                                                                updated.findIndex(
                                                                  (p) =>
                                                                    p.productId ===
                                                                    item.productId &&
                                                                    p.systemId ===
                                                                    item.systemId &&
                                                                    p.phaseId ===
                                                                    item.phaseId
                                                                );
                                                              if (
                                                                targetIndex !==
                                                                -1
                                                              ) {
                                                                updated[
                                                                  targetIndex
                                                                ] = {
                                                                  ...updated[
                                                                  targetIndex
                                                                  ],
                                                                  rate: "",
                                                                  amount:
                                                                    parseFloat(
                                                                      "0.00"
                                                                    ),
                                                                };
                                                              }
                                                              return updated;
                                                            }
                                                          );
                                                          return;
                                                        }

                                                        if (
                                                          !/^\d*\.?\d{0,2}$/.test(
                                                            inputValue
                                                          )
                                                        ) {
                                                          return;
                                                        }

                                                        const [integerPart] =
                                                          inputValue.split(".");

                                                        if (
                                                          integerPart &&
                                                          integerPart.length >
                                                          13
                                                        ) {
                                                          return;
                                                        }

                                                        const numericValue =
                                                          parseFloat(
                                                            inputValue
                                                          );

                                                        if (
                                                          numericValue >
                                                          9999999999999.99
                                                        ) {
                                                          return;
                                                        }

                                                        setFieldValue(
                                                          `items.${originalIndex}.rate`,
                                                          inputValue
                                                        );

                                                        const updatedAmount =
                                                          inputValue &&
                                                            item.quantity
                                                            ? (
                                                              parseFloat(
                                                                inputValue
                                                              ) *
                                                              item.quantity
                                                            ).toFixed(2)
                                                            : "0.00";
                                                        setFieldValue(
                                                          `items.${originalIndex}.amount`,
                                                          parseFloat(
                                                            updatedAmount
                                                          )
                                                        );
                                                        setSelectedAddedProducts(
                                                          (prevProducts) => {
                                                            const updated = [
                                                              ...prevProducts,
                                                            ];
                                                            const targetIndex =
                                                              updated.findIndex(
                                                                (p) =>
                                                                  p.productId ===
                                                                  item.productId &&
                                                                  p.systemId ===
                                                                  item.systemId &&
                                                                  p.phaseId ===
                                                                  item.phaseId
                                                              );
                                                            if (
                                                              targetIndex !== -1
                                                            ) {
                                                              updated[
                                                                targetIndex
                                                              ] = {
                                                                ...updated[
                                                                targetIndex
                                                                ],
                                                                rate: parseFloat(
                                                                  inputValue
                                                                ),
                                                                amount:
                                                                  parseFloat(
                                                                    updatedAmount
                                                                  ),
                                                              };
                                                            }
                                                            return updated;
                                                          }
                                                        );
                                                      }}
                                                      style={{
                                                        width: "200px",
                                                        fontSize: "0.8rem",
                                                      }}
                                                    />

                                                    <ErrorMessage
                                                      name={`items.${originalIndex}.rate`}
                                                      component="div"
                                                      className="text-danger"
                                                    />
                                                    {errors?.items?.[
                                                      originalIndex
                                                    ]?.rate && (
                                                        <span>
                                                          {
                                                            errors?.items[
                                                              originalIndex
                                                            ]?.rate
                                                          }
                                                          <br />
                                                        </span>
                                                      )}
                                                  </td>
                                                  <td
                                                    style={{ width: "200px" }}
                                                  >
                                                    <Form.Control
                                                      type="text"
                                                      step="0.01"
                                                      min={0}
                                                      pattern="^\d+(\.\d{0,2})?$"
                                                      name={`items.${originalIndex}.amount`}
                                                      value={item.amount || 0}
                                                      readOnly
                                                      style={{
                                                        width: "200px",
                                                        fontSize: "0.8rem",
                                                        cursor: "not-allowed",
                                                      }}
                                                    />
                                                    <ErrorMessage
                                                      name={`items.${originalIndex}.amount`}
                                                      component="div"
                                                      className="text-danger"
                                                    />
                                                    {errors?.items?.[
                                                      originalIndex
                                                    ]?.amount && (
                                                        <span>
                                                          {
                                                            errors?.items[
                                                              originalIndex
                                                            ]?.amount
                                                          }
                                                          <br />
                                                        </span>
                                                      )}
                                                  </td>

                                                  <td>
                                                    <div
                                                      title={
                                                        item.systemId
                                                          ? optionsSystem.find(
                                                            (option) =>
                                                              option.value ===
                                                              item.systemId
                                                          )?.label
                                                          : ""
                                                      }
                                                    >
                                                      <Select
                                                        closeMenuOnScroll={(
                                                          e
                                                        ) => {
                                                          try {
                                                            const target =
                                                              e.target;

                                                            if (
                                                              !target ||
                                                              !target.nodeType ||
                                                              typeof target.closest !==
                                                              "function"
                                                            ) {
                                                              return true;
                                                            }

                                                            const isMenuScroll =
                                                              target.closest(
                                                                '[class*="MenuList"]'
                                                              ) ||
                                                              target.closest(
                                                                '[class*="menu"]'
                                                              );

                                                            return !isMenuScroll;
                                                          } catch (error) {
                                                            return true;
                                                          }
                                                        }}
                                                        name={`items.${originalIndex}.systemId`}
                                                        options={optionsSystem}
                                                        value={optionsSystem.find(
                                                          (option) =>
                                                            option.value ===
                                                            item.systemId
                                                        )}
                                                        onChange={(
                                                          selectedOption
                                                        ) => {
                                                          const newSystemId =
                                                            selectedOption?.value ||
                                                            null;
                                                          handleSystemPhaseChange(
                                                            values.items,
                                                            originalIndex,
                                                            "systemId",
                                                            newSystemId,
                                                            setFieldValue
                                                          );
                                                        }}
                                                        isClearable
                                                        placeholder={
                                                          optionsSystem.length ===
                                                            0
                                                            ? "No data found"
                                                            : "Select System"
                                                        }
                                                        noOptionsMessage={() =>
                                                          "No data found"
                                                        }
                                                        menuPortalTarget={
                                                          document.body
                                                        }
                                                        menuPosition="fixed"
                                                        menuPlacement="auto"
                                                        styles={{
                                                          container: (
                                                            provided
                                                          ) => ({
                                                            ...provided,
                                                            width: "250px",
                                                          }),
                                                          option: (
                                                            provided,
                                                            state
                                                          ) => ({
                                                            ...provided,
                                                            backgroundColor:
                                                              state.isSelected
                                                                ? "#007bff"
                                                                : state.isFocused
                                                                  ? "#e6f7ff"
                                                                  : "#fff",
                                                            color: "#333",
                                                            fontSize: "0.8rem",
                                                          }),
                                                          menuPortal: (
                                                            provided
                                                          ) => ({
                                                            ...provided,
                                                            zIndex: 9999,
                                                          }),
                                                          menu: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999,
                                                          }),
                                                          control: (
                                                            provided
                                                          ) => ({
                                                            ...provided,
                                                            color: "#5e667b",
                                                          }),
                                                          placeholder: (
                                                            provided
                                                          ) => ({
                                                            ...provided,
                                                            whiteSpace:
                                                              "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow:
                                                              "ellipsis",
                                                          }),
                                                        }}
                                                      />
                                                      <ErrorMessage
                                                        name={`items.${originalIndex}.systemId`}
                                                        component="div"
                                                        className="text-danger"
                                                      />
                                                      {errors?.items?.[
                                                        originalIndex
                                                      ]?.systemId && (
                                                          <span>
                                                            {
                                                              errors?.items[
                                                                originalIndex
                                                              ]?.systemId
                                                            }
                                                            <br />
                                                          </span>
                                                        )}
                                                      {backendProductErrors?.errors
                                                        ?.filter(
                                                          (error) =>
                                                            error.id ===
                                                            item.systemId
                                                        )
                                                        .map((error, idx) => (
                                                          <span
                                                            key={idx}
                                                            className="text-danger"
                                                            style={{
                                                              fontSize:
                                                                "0.75rem",
                                                            }}
                                                          >
                                                            {error.message}
                                                            <br />
                                                          </span>
                                                        ))}
                                                    </div>
                                                  </td>

                                                  <td>
                                                    <div
                                                      title={
                                                        item.phaseId
                                                          ? getFilteredPhasesForSystem(
                                                            item.systemId
                                                          ).find(
                                                            (option) =>
                                                              option.value ===
                                                              item.phaseId
                                                          )?.label
                                                          : ""
                                                      }
                                                    >
                                                      <Select
                                                        closeMenuOnScroll={(
                                                          e
                                                        ) => {
                                                          try {
                                                            const target =
                                                              e.target;

                                                            if (
                                                              !target ||
                                                              !target.nodeType ||
                                                              typeof target.closest !==
                                                              "function"
                                                            ) {
                                                              return true;
                                                            }

                                                            const isMenuScroll =
                                                              target.closest(
                                                                '[class*="MenuList"]'
                                                              ) ||
                                                              target.closest(
                                                                '[class*="menu"]'
                                                              );

                                                            return !isMenuScroll;
                                                          } catch (error) {
                                                            return true;
                                                          }
                                                        }}
                                                        name={`items.${originalIndex}.phaseId`}
                                                        options={getFilteredPhasesForSystem(
                                                          item.systemId
                                                        )}
                                                        value={
                                                          item.systemId &&
                                                            item.phaseId
                                                            ? getFilteredPhasesForSystem(
                                                              item.systemId
                                                            ).find(
                                                              (option) =>
                                                                option.value ===
                                                                item.phaseId
                                                            )
                                                            : null
                                                        }
                                                        onChange={(
                                                          selectedOption
                                                        ) => {
                                                          const newPhaseId =
                                                            selectedOption?.value ||
                                                            null;
                                                          handleSystemPhaseChange(
                                                            values.items,
                                                            originalIndex,
                                                            "phaseId",
                                                            newPhaseId,
                                                            setFieldValue
                                                          );
                                                        }}
                                                        isClearable
                                                        placeholder={
                                                          !item.systemId
                                                            ? "First select a system"
                                                            : getFilteredPhasesForSystem(
                                                              item.systemId
                                                            ).length === 0
                                                              ? "No data found"
                                                              : "Select phase"
                                                        }
                                                        noOptionsMessage={() =>
                                                          !item.systemId
                                                            ? "First select a system"
                                                            : "No data found"
                                                        }
                                                        isDisabled={
                                                          !item.systemId
                                                        }
                                                        menuPortalTarget={
                                                          document.body
                                                        }
                                                        menuPosition="fixed"
                                                        menuPlacement="auto"
                                                        styles={{
                                                          container: (
                                                            provided
                                                          ) => ({
                                                            ...provided,
                                                            width: "280px",
                                                          }),
                                                          option: (
                                                            provided,
                                                            state
                                                          ) => ({
                                                            ...provided,
                                                            backgroundColor:
                                                              state.isSelected
                                                                ? "#007bff"
                                                                : state.isFocused
                                                                  ? "#e6f7ff"
                                                                  : "#fff",
                                                            color: "#333",
                                                            fontSize: "0.8rem",
                                                          }),
                                                          menuPortal: (
                                                            provided
                                                          ) => ({
                                                            ...provided,
                                                            zIndex: 9999,
                                                          }),
                                                          menu: (provided) => ({
                                                            ...provided,
                                                            zIndex: 9999,
                                                          }),
                                                          control: (
                                                            provided
                                                          ) => ({
                                                            ...provided,
                                                            color: "#5e667b",
                                                            backgroundColor:
                                                              !item.systemId
                                                                ? "#e9ecef"
                                                                : "#fff",
                                                          }),
                                                          placeholder: (
                                                            provided
                                                          ) => ({
                                                            ...provided,
                                                            whiteSpace:
                                                              "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow:
                                                              "ellipsis",
                                                          }),
                                                        }}
                                                      />
                                                      <ErrorMessage
                                                        name={`items.${originalIndex}.phaseId`}
                                                        component="div"
                                                        className="text-danger"
                                                      />
                                                      {errors?.items?.[
                                                        originalIndex
                                                      ]?.phaseId && (
                                                          <span>
                                                            {
                                                              errors?.items[
                                                                originalIndex
                                                              ]?.phaseId
                                                            }
                                                            <br />
                                                          </span>
                                                        )}
                                                      {backendProductErrors?.errors
                                                        ?.filter(
                                                          (error) =>
                                                            error.id ===
                                                            item.phaseId
                                                        )
                                                        .map((error, idx) => (
                                                          <span
                                                            key={idx}
                                                            className="text-danger"
                                                            style={{
                                                              fontSize:
                                                                "0.75rem",
                                                            }}
                                                          >
                                                            {error.message}
                                                            <br />
                                                          </span>
                                                        ))}
                                                    </div>
                                                  </td>

                                                  <td
                                                    style={{
                                                      textWrap: "unset",
                                                    }}
                                                  >
                                                    <Link
                                                      className={`btn btn-sm btn-icon btn-success`}
                                                      data-toggle="tooltip"
                                                      data-placement="top"
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        const selectedProductIds =
                                                          values.items
                                                            .map((item, i) =>
                                                              i !==
                                                                originalIndex
                                                                ? item.productId
                                                                : null
                                                            )
                                                            .filter((id) => id);
                                                        handleView(
                                                          e,
                                                          values.items[
                                                          originalIndex
                                                          ],
                                                          selectedProductIds,
                                                          originalIndex,
                                                          values
                                                        );
                                                      }}
                                                      title="View"
                                                      data-original-title="View"
                                                    >
                                                      <span className="btn-inner">
                                                        <IoEyeOutline
                                                          style={{
                                                            width: "20px",
                                                            height: "20px",
                                                          }}
                                                        />
                                                      </span>
                                                    </Link>{" "}
                                                    <Link>
                                                      <Tooltip title="Remove">
                                                        <IconButton
                                                          size="medium"
                                                          color="error"
                                                          onClick={() => {
                                                            arrayHelpers.remove(
                                                              originalIndex
                                                            );
                                                            setSelectedAddedProducts(
                                                              (prevSelected) =>
                                                                prevSelected.filter(
                                                                  (p) =>
                                                                    !(
                                                                      p.productId ===
                                                                      item.productId &&
                                                                      p.systemId ===
                                                                      item.systemId &&
                                                                      p.phaseId ===
                                                                      item.phaseId
                                                                    )
                                                                )
                                                            );
                                                          }}
                                                        >
                                                          <CancelIcon fontSize="medium" />
                                                        </IconButton>
                                                      </Tooltip>
                                                    </Link>
                                                  </td>
                                                </tr>
                                              );
                                            }
                                          )}
                                          <tr>
                                            {paginatedItems.length > 0 &&
                                              showLoadMore && (
                                                <div
                                                  style={{
                                                    position: "sticky",
                                                    bottom: "0",
                                                    left: "45%",
                                                    width: "100%",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    backgroundColor: "white",
                                                    padding: "1rem 0",
                                                    zIndex: 10,
                                                  }}
                                                >
                                                  <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-lg px-4 py-2 mb-2"
                                                    onClick={() => {
                                                      setIsLoadingMoreSelectedProduct(
                                                        true
                                                      );
                                                      loadMoreItems();
                                                      setTimeout(() => {
                                                        setIsLoadingMoreSelectedProduct(
                                                          false
                                                        );
                                                      }, 300);
                                                    }}
                                                    disabled={
                                                      isLoadingMoreSelectedProduct
                                                    }
                                                    style={{
                                                      borderRadius: "20px",
                                                      fontWeight: "400",
                                                      fontSize: "14px",
                                                      minWidth: "150px",
                                                      transition:
                                                        "all 0.3s ease",
                                                    }}
                                                  >
                                                    {isLoadingMoreSelectedProduct ? (
                                                      <>
                                                        <span className="spinner-border spinner-border-sm me-2" />
                                                        Loading...
                                                      </>
                                                    ) : (
                                                      <>
                                                        <i className="fas fa-plus-circle me-2" />
                                                        Load More
                                                      </>
                                                    )}
                                                  </button>
                                                </div>
                                              )}

                                            {(values.items.length === 0 ||
                                              paginatedItems.length === 0 ||
                                              (values.items.length > 0 &&
                                                searchSelectedProducts.trim()
                                                  .length > 0 &&
                                                paginatedItems.length ===
                                                0)) && (
                                                <td colSpan="12">
                                                  <div
                                                    style={{
                                                      padding: "3rem 0",
                                                    }}
                                                  >
                                                    <div
                                                      className="d-flex flex-column align-items-left text-muted"
                                                      style={{
                                                        marginLeft: "20rem",
                                                      }}
                                                    >
                                                      <i className="fas fa-search fa-3x mb-3 opacity-50"></i>
                                                      {values?.items?.length ===
                                                        0 ? (
                                                        <>
                                                          <span>
                                                            No products have been
                                                            added yet.{" "}
                                                            <span
                                                              style={{
                                                                color: "#007bff",
                                                                cursor: "pointer",
                                                              }}
                                                              onClick={async () => {
                                                                setSelectedProductListIds(
                                                                  []
                                                                );
                                                                setSelectedAddedProducts(
                                                                  values.items
                                                                );
                                                                setProductsViewModel(
                                                                  true
                                                                );
                                                                setSearchSelectedProducts(
                                                                  ""
                                                                );
                                                                setSelectedSystemForFilter(
                                                                  null
                                                                );
                                                                setSelectedPhaseForFilter(
                                                                  null
                                                                );
                                                              }}
                                                            >
                                                              Add some products
                                                            </span>{" "}
                                                            to continue.
                                                          </span>
                                                        </>
                                                      ) : (
                                                        paginatedItems.length ===
                                                        0 && (
                                                          <p className="mb-0">
                                                            No products found. Try
                                                            adjusting your search
                                                            criteria.
                                                          </p>
                                                        )
                                                      )}
                                                    </div>
                                                  </div>
                                                </td>
                                              )}
                                          </tr>
                                        </>
                                      );
                                    }}
                                  />
                                </tbody>
                              </Table>
                            </div>

                            <div className="d-flex flex-wrap flex-md-nowrap justify-content-end gap-1 gap-md-2 mt-4">
                              <Button
                                type="button"
                                className="me-2 me-md-5 px-3 px-md-4"
                                variant="btn btn-light"
                                onClick={() => {
                                  resetForm();
                                  setFieldValue("items", []);
                                  setSelectedAddedProducts([]);
                                }}
                              >
                                Reset
                              </Button>

                              <Button
                                type="submit"
                                disabled={isSubmitting}
                                variant="btn btn-primary"
                                className="me-2 me-md-5 px-3 px-md-4"
                              >
                                Submit
                              </Button>
                              <Button
                                type="button"
                                className=" px-3 px-md-4"
                                variant="btn btn-danger"
                                onClick={() => {
                                  if (hasUnsavedChanges) {
                                    setShowDiscardModal(true);
                                  } else {
                                    navigate(
                                      `/dashboard/app/job-edit/${jobId}`
                                    );
                                  }
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </>
                        ) : (
                          loader && <FadeLoaderComponent />
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};
export default JobBomAdd;
